import React, {Fragment, Suspense, useEffect, useState} from "react";
import {Route, Routes} from "react-router-dom";
import useMedia from "use-media";
import ErrorBoundary from "components/ErrorBoundary";
import {useCheckSignedIn} from "hooks/useCheckSignedIn";
import ErrorFallback from "components/fallbacks/ErrorFallback";
import {endpoints, GoogleAuthApiKey} from "config";
import api from "api";
import SignIn from "components/SignIn";
import {gapi} from 'gapi-script';
import NotFound from "components/NotFound";
import {useDispatch, useSelector} from "react-redux";
import {removeLeftMenu, selectCollapse, selectVisible, showLeftMenu} from "reducers/leftMenu";
import {showBurger, hideBurger, selectNavVisible} from "reducers/navHeader";
import {selectProjectId} from "reducers/client";
import {setPortalData} from "reducers/configuration";
import {hideFilterMenu, showFilterMenu} from "reducers/filterMenu";
import {Spinner} from "components/ui/Spinner";
import Settings from "./components/Settings";
import SsoSignIn from "./components/SsoSignIn";
import Print from "./components/Print";

const Projects = React.lazy(() => import("components/Projects"));
const ProjectView = React.lazy(() => import("components/ProjectView"));
const ResetPassword = React.lazy(() => import("components/ResetPassword"));
const ForgottenPassword = React.lazy(() =>
  import("components/ForgottenPassword")
);
const SetPassword = React.lazy(() => import("components/SetPassword"));
const NavHeader = React.lazy(() => import("components/NavHeader"));
const MobileNavHeader = React.lazy(() => import("components/MobileNavHeader"));
const LeftMenu = React.lazy(() => import("components/LeftMenu/LeftMenu"));
const LeftMenuLinks = React.lazy(() =>
  import("components/LeftMenu/LeftMenuLinks")
);
const MobileMenu = React.lazy(() => import("components/MobileMenu/MobileMenu"));
const MobileMenuLinks = React.lazy(() =>
  import("components/MobileMenu/MobileMenuLinks")
);

const Dashboard = React.lazy(() => import("components/Dashboard"));
const ReviewFeed = React.lazy(() => import("components/ReviewFeed"));
const ReviewConsolidation = React.lazy(() => import("components/ReviewConsolidation"));
const CommentAnalysis = React.lazy(() => import("components/CommentAnalysis"));
const LoginReport = React.lazy(() => import("components/LoginReport"));
const Downloads = React.lazy(() => import("components/Downloads"));
const Guides = React.lazy(() => import("components/Guides"));
const LeagueTables = React.lazy(() => import("components/LeagueTables"));
const Users = React.lazy(() => import("components/Users"));
const Account = React.lazy(() => import("components/Account"));
const Location = React.lazy(() =>
  import("components/Account/Location/Location")
);
const Landing = React.lazy(() => import("components/Landing"));
const Quicksight = React.lazy(() => import("components/Quicksight"));
const PdfReports = React.lazy(() => import("components/PdfReports"));

const portalURL = endpoints.portalConfiguration.getPortalConfiguration;

const App: React.FC = () => {
  const dispatch = useDispatch();
  const [menuCollapseDelay, setMenuCollapseDelay] = useState(false);
  const isAppVisible = useCheckSignedIn();
  const menuCollapsed = useSelector(selectCollapse);
  const menuVisible = useSelector(selectVisible);
  const navVisible = useSelector(selectNavVisible);
  const projectId = useSelector(selectProjectId);

  gapi.load('client:auth2', () => {
    gapi.auth2.init({
      clientId: GoogleAuthApiKey,
      plugin_name: 'chat',
    });
  });
  useEffect(() => {
    if (projectId)
      api
        .get(portalURL, {
          params: {
            projectId,
          },
        })
        .then(function (response) {
          if (response.data.details) {
            let visibleItems = 0;
            if (response.data.details.bDownloadsVisible)
              visibleItems += 1;
            if (response.data.details.bCommentAnalysisVisible)
              visibleItems += 1;
            if (response.data.details.bDashboardVisible)
              visibleItems += 1;
            if (response.data.details.bLeagueTablesVisible)
              visibleItems += 1;
            if (response.data.details.bLoginReportVisible)
              visibleItems += 1;
            if (response.data.details.bQuicksightVisible)
              visibleItems += 1;
            if (response.data.details.bReviewFeedVisible)
              visibleItems += 1;
            if (response.data.details.bReviewConsolidationVisible)
              visibleItems += 1;
            if (visibleItems < 2){
              dispatch(removeLeftMenu());
              dispatch(hideBurger());
            } else {
              dispatch(showLeftMenu());
              dispatch(showBurger());
            }
          }
          dispatch(setPortalData(response.data.details));
        });
  }, [projectId, dispatch]);

  useEffect(() => {
    setTimeout(() => {
      setMenuCollapseDelay(menuCollapsed);
    }, 300);
  }, [menuCollapsed]);

  const mobileBreakpoint = useMedia({maxWidth: 1024});

  React.useEffect(() => {
    if (mobileBreakpoint) {
      dispatch(hideFilterMenu());
    }
    // else {
    //   dispatch(showFilterMenu());
    // }
  }, [mobileBreakpoint, dispatch]);

  // At startup, we check to see if we should display a signin page or the content.
  // Return nothing, so that we don't flash a view of signin if unneccessary.
  if (!isAppVisible) return null;

  return (
    <ErrorBoundary fallback={<ErrorFallback/>}>
      <div className="Main">
        <Suspense fallback={<Spinner/>}>
          <Routes>
            <Route path="/" element={<Projects/>}/>
            <Route path="sign-in" element={<SignIn/>}/>
            <Route path="sso-sign-in" element={<SsoSignIn/>}/>
            <Route path="forgotten-password" element={<ForgottenPassword/>}/>
            <Route path="reset-password" element={<ResetPassword/>}/>
            <Route path="set-password" element={<SetPassword/>}/>
            <Route path="*" element={<Fragment/>}/>
          </Routes>
        </Suspense>
        {navVisible &&
            <Suspense fallback={<Spinner/>}>
                <Routes>
                    <Route path="project/*" element={!mobileBreakpoint ? <NavHeader/> : <MobileNavHeader/>}/>
                    <Route path="*" element={<Fragment/>}/>
                </Routes>
            </Suspense>
        }
        <div className="View">
          {menuVisible &&
            (!mobileBreakpoint ? (
                <Suspense fallback={<Spinner/>}>
                  <div style={{width: menuCollapseDelay ? "55px" : "220px"}}>
                  <Routes>
                    <Route path="project/:projectId" element={<LeftMenu/>}>
                      <Route path="*" element={<LeftMenuLinks/>}/>
                    </Route>
                    <Route path="*" element={<Fragment/>}/>
                  </Routes>
                  </div>
                </Suspense>
              ) : (

                  <Suspense fallback={<Spinner/>}>
                    <Routes>
                      <Route path="project/:projectId" element={<MobileMenu/>}>
                        <Route path="*" element={<MobileMenuLinks/>}/>
                      </Route>
                      <Route path="*" element={<Fragment/>}/>
                    </Routes>
                  </Suspense>
              )
            )
          }
          <Suspense fallback={<Spinner/>}>
            <Routes>
              <Route path="project/:projectId" element={<ProjectView/>}>
                <Route path="home" element={<Landing/>}/>
                <Route path="dashboard" element={<Dashboard/>}/>
                <Route path="print" element={<Print/>}/>
                <Route path="review-feed" element={<ReviewFeed/>}/>
                <Route path="review-consolidation" element={<ReviewConsolidation/>}/>
                <Route path="comment-analysis" element={<CommentAnalysis/>}/>
                <Route path="league-tables" element={<LeagueTables/>}/>
                <Route path="login-report" element={<LoginReport/>}/>
                <Route path="downloads" element={<Downloads/>}/>
                <Route path="guides" element={<Guides/>}/>
                <Route path="users" element={<Users/>}/>
                <Route path="account" element={<Account/>}/>
                <Route path="account/:locationId/" element={<Location/>}/>
                <Route path="settings" element={<Settings/>}/>
                <Route path="quicksight" element={<Quicksight/>}/>
                <Route path="pdf-reports" element={<PdfReports/>}/>
                <Route path="*" element={<NotFound/>}/>
              </Route>
              <Route path="*" element={<Fragment/>}/>
            </Routes>
          </Suspense>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default App;
